import {
  ClampToEdgeWrapping,
  DataTexture,
  FloatType,
  LinearFilter,
  NearestFilter,
  NoColorSpace,
  RGBAFormat,
  RepeatWrapping,
  UnsignedByteType,
} from 'three'

export const createChainTexture = (
  data: Float32Array,
  jointCount: number,
): DataTexture => {
  const texture = new DataTexture(
    data,
    jointCount,
    1,
    RGBAFormat,
    FloatType,
  )
  texture.minFilter = NearestFilter
  texture.magFilter = NearestFilter
  texture.wrapS = ClampToEdgeWrapping
  texture.wrapT = ClampToEdgeWrapping
  texture.generateMipmaps = false
  texture.needsUpdate = true
  return texture
}

const weaveHeight = (x: number, y: number): number => {
  const frequency = Math.PI * 8
  const warp = Math.sin(x * frequency) * (0.62 + 0.18 * Math.cos(y * frequency))
  const weft =
    Math.sin(y * frequency + Math.PI * 0.5) *
    (0.48 + 0.14 * Math.cos(x * frequency))
  return warp + weft
}

export const createFabricNormalTexture = (size: number): DataTexture => {
  const data = new Uint8Array(size * size * 4)
  const texel = 1 / size
  const derivativeStrength = 0.78

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = (x + 0.5) / size
      const v = (y + 0.5) / size
      const derivativeX =
        (weaveHeight(u + texel, v) - weaveHeight(u - texel, v)) *
        derivativeStrength
      const derivativeY =
        (weaveHeight(u, v + texel) - weaveHeight(u, v - texel)) *
        derivativeStrength
      const inverseLength =
        1 / Math.hypot(derivativeX, derivativeY, 1)
      const normalX = -derivativeX * inverseLength
      const normalY = -derivativeY * inverseLength
      const normalZ = inverseLength
      const offset = (y * size + x) * 4

      data[offset] = Math.round((normalX * 0.5 + 0.5) * 255)
      data[offset + 1] = Math.round((normalY * 0.5 + 0.5) * 255)
      data[offset + 2] = Math.round((normalZ * 0.5 + 0.5) * 255)
      data[offset + 3] = 255
    }
  }

  const texture = new DataTexture(
    data,
    size,
    size,
    RGBAFormat,
    UnsignedByteType,
  )
  texture.colorSpace = NoColorSpace
  texture.wrapS = RepeatWrapping
  texture.wrapT = RepeatWrapping
  texture.minFilter = LinearFilter
  texture.magFilter = LinearFilter
  texture.generateMipmaps = true
  texture.needsUpdate = true
  return texture
}
